// src/lib/janelaFaturamento.ts
//
// Regra de justificativa de boleto emitido fora do prazo padrão.
//
// O prazo é o mesmo "Crítico" que a Janela de Faturamento/Cobrança já usa
// (views/Financeiro/PendentesCalculoModal.tsx): `data_vencimento - 7 dias
// corridos`, ajustado para a segunda-feira seguinte se cair em fim de semana.
//
// Sete dias corridos e cinco dias ÚTEIS são a mesma data sempre que o vencimento
// cai em dia de semana — é por isso que a regra é enunciada pelo Faturamento como
// "5 dias antes do vencimento" e pelo código como "-7". Exemplo do chamado:
// vencimento 15/09/2026 (terça) → 15/09 menos 5 dias úteis = 08/09; 15/09 menos 7
// dias corridos = 08/09. Emitir em 09/09 ou depois exige justificativa.
//
// Uma definição só, importada tanto pela Janela quanto pelo gate de emissão, para
// que mudar o limiar não deixe as duas telas discordando.

import { addDaysAndAdjust, diffInDays } from './janelaCobranca.ts';

/**
 * Dias corridos antes do `data_vencimento` da fatura da distribuidora em que a
 * emissão ainda é considerada dentro do prazo. Equivale a 5 dias úteis.
 */
export const DIAS_CRITICO_ANTES_VENCIMENTO = 7;

/** Motivos aceitos para uma emissão fora do prazo. A ordem é a que aparece no select. */
export const MOTIVOS_ATRASO_EMISSAO = [
  'Fatura não estava disponível na Equatorial',
  'Aguardando solicitação de refaturamento',
  'Houve troca de titularidade',
  'Dados incorretos no sistema',
  'Fatura não caiu no e-mail',
  'Outro',
] as const;

export type MotivoAtrasoEmissao = (typeof MOTIVOS_ATRASO_EMISSAO)[number];

/** O único motivo que torna o texto livre obrigatório. */
export const MOTIVO_OUTRO: MotivoAtrasoEmissao = 'Outro';

/**
 * Último dia em que a emissão ainda está no prazo padrão.
 * É o mesmo `criticoJanelaIso` da Janela de Faturamento.
 */
export const limiteEmissaoNoPrazo = (dataVencimento: string | null | undefined): Date | null => {
  if (!dataVencimento) return null;
  const limite = addDaysAndAdjust(dataVencimento, -DIAS_CRITICO_ANTES_VENCIMENTO);
  if (!limite || isNaN(limite.getTime())) return null;
  return limite;
};

/** `true` quando a fatura é um boleto avulso (sem prazo de faturamento a cumprir). */
export const ehBoletoAvulso = (classificacao?: string | null): boolean =>
  (classificacao || '').trim().toUpperCase() === 'AVULSO';

/**
 * Uma emissão anterior na mesma UC + mês conta como "o boleto do mês já saiu"?
 *
 * Só quando a linha anterior NÃO é avulsa. Boleto avulso é cobrança à parte (acerto,
 * saldo de cancelamento, parcelamento) e não substitui o boleto do mês — tratá-lo
 * como emissão anterior dispensava a fatura real do motivo de atraso para sempre.
 *
 * Caso real: UC 337787501263, AGO/2026. A fatura 9518 (AVULSO) saiu em 03/08; a
 * fatura 11131 saiu em 08/09, 13 dias depois do limite, e não pediu motivo porque a
 * avulsa a fez parecer reemissão.
 */
export const houveEmissaoDoBoletoDoMes = (
  outrasLinhasEmitidas: { classificacao?: string | null }[] | null | undefined,
): boolean => (outrasLinhasEmitidas ?? []).some((l) => !ehBoletoAvulso(l.classificacao));

export interface ContextoEmissao {
  /**
   * `faturas.data_vencimento` — o vencimento da fatura da EQUATORIAL, não o
   * `vencimento_boleto` da Vigor. É sobre ele que a Janela de Faturamento raciocina.
   */
  dataVencimento?: string | null;
  /** Instante da emissão. Default: agora. */
  dataEmissao?: Date;
  /** `faturas.classificacao`. 'AVULSO' não tem prazo de faturamento a cumprir. */
  classificacao?: string | null;
  /**
   * Limite de emissão do AGRUPAMENTO ("YYYY-MM-DD"), quando a UC está em um agrupamento de
   * faturamento ativo (#5787). Presente, ele SUBSTITUI o limite individual: o prazo dessas UCs
   * é o do contrato (último dia útil do mês), calculado em src/lib/reguaPrazosAgrupamento.ts.
   *
   * ⚠️ Substituir não é desligar. Emitir depois do limite do agrupamento continua sendo atraso
   * e continua pedindo justificativa — o que deixa de existir é o atraso falso das UCs que
   * vencem no fim do mês.
   */
  limiteAgrupamentoIso?: string | null;
  /**
   * `faturas.historico_emissoes` (append-only, gravado por trigger). Qualquer
   * entrada significa que esta fatura JÁ foi emitida antes — logo é reemissão.
   */
  historicoEmissoes?: { emitido_em?: string | null }[] | null;
  /** `faturas.emitido_em` — carimbo do boleto vivo. Presente = já houve emissão. */
  emitidoEm?: string | null;
  /**
   * Reemissão que nasceu numa LINHA NOVA de `faturas` (cancelamento seguido de um
   * novo lançamento para a mesma UC + mês de referência). Nesses casos o histórico
   * da linha nova nasce vazio e sozinho não denuncia a reemissão — quem chama
   * precisa olhar as outras linhas da mesma UC/mês e informar aqui.
   *
   * Passe o resultado de `houveEmissaoDoBoletoDoMes`, não um `length > 0` cru: linha
   * avulsa emitida antes não conta, e contá-la dispensa a fatura real para sempre.
   */
  houveEmissaoAnteriorNaUcEMes?: boolean;
}

export type DispensaJustificativa =
  | 'no_prazo'
  | 'sem_vencimento'
  | 'avulso'
  | 'reemissao'
  /**
   * UC dentro de um agrupamento de faturamento ATIVO (#5787). O prazo dessas UCs é o do
   * contrato do agrupamento — último dia útil do mês —, não o vencimento da distribuidora
   * menos 7 dias. Em AGO/2026, 114 das 117 faturas da Prefeitura vencem em 30/09: manter a
   * régua individual pediria justificativa em todas elas por emitir exatamente no prazo
   * combinado. Quem cobra o prazo delas é src/lib/reguaPrazosAgrupamento.ts.
   */
  | 'agrupamento';

export type ResultadoExigencia =
  | { exige: false; dispensa: DispensaJustificativa; limite: Date | null }
  | { exige: true; limite: Date; diasDeAtraso: number };

/**
 * `true` quando esta emissão é, na verdade, uma REEMISSÃO — o boleto já saiu antes
 * para a mesma UC e mês de referência, seja na mesma linha de `faturas` (o caminho
 * normal: cancelar e emitir de novo) seja numa linha nova.
 */
export const ehReemissao = (ctx: ContextoEmissao): boolean => {
  if (ctx.houveEmissaoAnteriorNaUcEMes) return true;
  if ((ctx.historicoEmissoes?.length ?? 0) > 0) return true;
  return !!ctx.emitidoEm;
};

/**
 * Decide se esta emissão precisa de motivo + justificativa.
 *
 * A ordem das dispensas importa para o relatório: uma reemissão atrasada é
 * dispensada como 'reemissao', não como 'no_prazo'.
 */
export const exigeJustificativaAtraso = (ctx: ContextoEmissao): ResultadoExigencia => {
  // UC de agrupamento tem prazo próprio: o do contrato, não o vencimento da distribuidora − 7.
  const limiteAgrupamento = ctx.limiteAgrupamentoIso
    ? new Date(`${ctx.limiteAgrupamentoIso.slice(0, 10)}T23:59:59`)
    : null;
  const limite = limiteAgrupamento && !isNaN(limiteAgrupamento.getTime())
    ? limiteAgrupamento
    : limiteEmissaoNoPrazo(ctx.dataVencimento);

  if (ehBoletoAvulso(ctx.classificacao)) return { exige: false, dispensa: 'avulso', limite };
  if (ehReemissao(ctx)) return { exige: false, dispensa: 'reemissao', limite };
  if (!limite) return { exige: false, dispensa: 'sem_vencimento', limite: null };

  const emissao = ctx.dataEmissao ?? new Date();
  const diasDeAtraso = diffInDays(emissao.toISOString(), limite.toISOString());

  // `> 0`, e não `>= 0`: o dia do limite ainda está no prazo. No exemplo do
  // chamado (vencimento 15/09, limite 08/09) emitir em 08/09 é pontual e emitir
  // em 09/09 atrasa em 1 dia.
  if (diasDeAtraso === null || diasDeAtraso <= 0) {
    // Dentro do prazo: quando o prazo veio do agrupamento, a dispensa diz isso, para o relatório
    // separar "no prazo individual" de "no prazo do contrato".
    return { exige: false, dispensa: limiteAgrupamento ? 'agrupamento' : 'no_prazo', limite };
  }

  return { exige: true, limite, diasDeAtraso };
};

/**
 * Valida o par motivo/justificativa. O texto livre só é obrigatório em "Outro".
 * Devolve `null` quando está válido, ou a mensagem do que falta.
 */
export const validarJustificativaAtraso = (
  motivo: string | null | undefined,
  justificativa: string | null | undefined,
): string | null => {
  const m = (motivo || '').trim();
  if (!m) return 'Selecione o motivo da emissão fora do prazo.';
  if (!(MOTIVOS_ATRASO_EMISSAO as readonly string[]).includes(m)) return 'Motivo inválido.';
  if (m === MOTIVO_OUTRO && !(justificativa || '').trim()) {
    return 'Descreva a justificativa quando o motivo for "Outro".';
  }
  return null;
};
